/**
 * orientation.ts — THE single orientation convention for the whole game.
 *
 * This file exists because getting the sign wrong here was the single biggest
 * bug in the prototype: the car rendered facing the mirror of its travel
 * direction and looked like it was crabbing sideways. Everything — cars, the
 * track centreline, the finish gate, AI aiming — routes through these helpers,
 * so the convention can never quietly drift apart again.
 *
 * CONVENTION:
 *   - A heading `h` (radians) points in world direction (sin h, cos h) on the
 *     (x, z) ground plane.
 *   - h = 0 faces +Z. Increasing h rotates toward +X.
 *   - A mesh whose "front" is local +Z is oriented with `mesh.rotation.y = +h`
 *     (a PLUS h, not minus h).
 *
 * NOTE: screen-relative *player* steering (flipping the input sign so "right on
 * the stick" = "right on screen") is handled in the input layer, NOT here. The
 * AI plans in world space and uses these helpers unflipped.
 */

/** X component of the forward direction for heading `h`. */
export function forwardX(h: number): number {
  return Math.sin(h);
}

/** Z component of the forward direction for heading `h`. */
export function forwardZ(h: number): number {
  return Math.cos(h);
}

/** The heading that points along the ground vector (dx, dz). Inverts forwardX/Z. */
export function headingFromForward(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

/**
 * X component of the rightward direction (the geometric right side of a car
 * facing heading `h`). Perpendicular to forward; right(0) = +X. Used for road
 * width, wall placement, and the lateral wall-clamp normal.
 */
export function rightX(h: number): number {
  return Math.cos(h);
}

/** Z component of the rightward direction for heading `h`. right(0) = +X. */
export function rightZ(h: number): number {
  return -Math.sin(h);
}
